/**
 * ContractorDocsSendBundle · iter303ak-fix28 — Work Booked · T70 · Action 20
 * ─────────────────────────────────────────────────────────────────────────
 * MOCKED bundled send of Work Sheet + Contractor Sheet + Office Contractor
 * Sheet per contractor. Recipient email editable inline. Stamps
 * contractor_docs_sent_at.{child_id}.{contractor_id} on the parent appraisal.
 *
 * Writes to captured_data:
 *   contractor_docs_send_state: {
 *     [`${child_id}|${contractor_id}`]: { sent_at, to_email, ok, error? }
 *   }
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { sendWorkflowEmail, jobRef, signOff } from './_emailSend';
import { CheckCircle2, Mail, AlertTriangle, Send, User, FileText } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const isEmailValid = (e) => !!(e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim()));

export default function ContractorDocsSendBundle({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const stampFieldPrefix = params.stamp_field_prefix || 'contractor_docs_sent_at';

  // Items = previous action's include=true rows.
  const items = useMemo(() => {
    const prev = capturedData?.contractor_docs_preview_selection || {};
    const rawItems = Array.isArray(prev.items) ? prev.items : [];
    const include = prev.include || {};
    return rawItems.filter(it => include[`${it.child_id}|${it.contractor_id}`] !== false);
  }, [capturedData]);

  const [rowState, setRowState] = useState(() => value?.row_state || {});
  const [sendState, setSendState] = useState(() => value?.send_state || {});
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [globalErr, setGlobalErr] = useState('');
  const [enriched, setEnriched] = useState(false);

  const enrichRows = useCallback(async () => {
    if (!items.length || enriched) return;
    const next = { ...rowState };
    for (const it of items) {
      const key = `${it.child_id}|${it.contractor_id}`;
      if (next[key] && next[key].email !== undefined) continue;
      let email = '';
      try {
        const { data: cdoc } = await api.get(`/contractors/${it.contractor_id}`);
        email = cdoc?.email || '';
      } catch { /* silent */ }
      next[key] = { email, email_orig: email };
    }
    setRowState(next);
    setEnriched(true);
  }, [items, enriched]);   

  useEffect(() => { enrichRows(); }, [enrichRows]);

  useEffect(() => {
    if (onChange) onChange({ send_state: sendState, row_state: rowState });
  }, [sendState, rowState]);   

  const updateRow = (key, patch) => setRowState(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const isSent = (key) => !!sendState[key]?.sent_at;

  const sendOne = async (child_id, contractor_id) => {
    const key = `${child_id}|${contractor_id}`;
    const row = rowState[key] || {};
    if (!isEmailValid(row.email)) {
      setSendState(prev => ({ ...prev, [key]: { error: 'Enter a valid email above before sending.' } }));
      return;
    }
    setBusyKeys(prev => { const n = new Set(prev); n.add(key); return n; });
    setGlobalErr('');
    try {
      const it = items.find(x => x.child_id === child_id && x.contractor_id === contractor_id) || {};
      await sendWorkflowEmail(api, {
        to: (row.email || '').trim(),
        subject: `Job documents – ${jobRef(it)} – Roofs To Remedy Ltd`,
        body: `Hi ${it.contractor_name || ''},\n\nPlease find the job documents attached for ${jobRef(it)}.${signOff}`,
        attachments: [
          { route: `/appraisals/${child_id}/contractor-pdf/${encodeURIComponent(it.ctype || 'All')}`, filename: `Contractor_Report_${it.appraisal_no || child_id.slice(-6)}` },
          { route: `/reports/contractor-sheet/${child_id}`, filename: `Contractor_Sheet_${it.appraisal_no || child_id.slice(-6)}` },
        ],
        appraisalId: child_id, event: 'contractor_docs',
      });
      const nowIso = new Date().toISOString();
      if (parentAppraisalId) {
        const existing = capturedData?.appraisal?.[stampFieldPrefix]
          || capturedData?.appraisal_record?.[stampFieldPrefix]
          || {};
        await api.patch(`/appraisals/${parentAppraisalId}`, {
          [stampFieldPrefix]: {
            ...existing,
            [child_id]: {
              ...(existing[child_id] || {}),
              [contractor_id]: nowIso,
            },
          },
        });
      }
      setSendState(prev => ({
        ...prev,
        [key]: { sent_at: nowIso, ok: true, to_email: (row.email || '').trim() },
      }));
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.message || 'Send failed';
      setSendState(prev => ({ ...prev, [key]: { error: errMsg } }));
      setGlobalErr(errMsg);
    } finally {
      setBusyKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const sendAll = async () => {
    for (const it of items) {
      const key = `${it.child_id}|${it.contractor_id}`;
      if (isSent(key)) continue;
      if (!isEmailValid((rowState[key] || {}).email)) continue;
      await sendOne(it.child_id, it.contractor_id);
    }
  };

  const allSent = items.length > 0 && items.every(i => isSent(`${i.child_id}|${i.contractor_id}`));
  const sentCount = items.filter(i => isSent(`${i.child_id}|${i.contractor_id}`)).length;

  return (
    <div className="space-y-4" data-testid="wf-contractor-docs-send">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Send Contractor Docs Bundle</div>
            <div className="text-sm mt-1 text-amber-800">
              {params.prompt || 'Send each contractor their bundle (Work Sheet + Contractor Sheet + Office Sheet).'}
            </div>
          </div>
        </div>
        <div className="mt-3 p-2 rounded bg-white border border-amber-200 text-[11px] text-amber-800">
          Sends a real email with the contractor report and contractor sheet attached.
        </div>
      </div>

      {items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          Nothing to send — no rows were included in the previous preview action.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-600">
              <b className="text-slate-800">{sentCount}</b> of <b className="text-slate-800">{items.length}</b> sent
            </div>
            <button type="button" onClick={sendAll} disabled={allSent || busyKeys.size > 0}
              data-testid="wf-cds-send-all"
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5">
              <Send size={12} /> {allSent ? 'All sent ✓' : (busyKeys.size > 0 ? 'Sending…' : 'Send all')}
            </button>
          </div>

          <div className="space-y-2">
            {items.map(it => {
              const key = `${it.child_id}|${it.contractor_id}`;
              const row = rowState[key] || {};
              const sent = isSent(key);
              const err = sendState[key]?.error;
              const busy = busyKeys.has(key);
              const emailValid = isEmailValid(row.email);
              const label = `${it.quote_type_ref || ''}${it.variant_label ? ` · ${it.variant_label}` : ''} — ${it.contractor_name || it.contractor_id}`;
              return (
                <div key={key} className={`p-3 border rounded-lg ${sent ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`}
                  data-testid={`wf-cds-row-${it.child_id}-${it.contractor_id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-sm font-medium text-slate-800">{label}</span>
                    {it.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{it.appraisal_no}</span>}
                    {sent && (
                      <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                        <CheckCircle2 size={10} /> Sent
                      </span>
                    )}
                  </div>
                  {it.contractor_name && (
                    <div className="text-[11px] text-slate-600 mb-2 flex items-center gap-1">
                      <User size={11} /> {it.contractor_name}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2 pl-3 border-l-2 border-slate-200">
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Email <span className="text-rose-500">*</span></label>
                      <input type="email" value={row.email || ''}
                        onChange={(e) => updateRow(key, { email: e.target.value })}
                        disabled={sent} placeholder="contractor@email.com"
                        data-testid={`wf-cds-email-${it.child_id}-${it.contractor_id}`}
                        className={`w-full px-2 py-1 text-xs border rounded ${row.email && !emailValid ? 'border-rose-400 bg-rose-50' : 'border-slate-300'} disabled:opacity-60`} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] text-slate-500">
                      {sent && <span className="text-emerald-700">✓ Stamped {new Date(sendState[key].sent_at).toLocaleString()} → {sendState[key].to_email}</span>}
                      {err && <span className="text-rose-700">Error: {err}</span>}
                    </div>
                    <button type="button" onClick={() => sendOne(it.child_id, it.contractor_id)}
                      disabled={sent || busy || !emailValid}
                      data-testid={`wf-cds-send-btn-${it.child_id}-${it.contractor_id}`}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5">
                      <Send size={12} /> {sent ? 'Sent' : (busy ? 'Sending…' : 'Send')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {globalErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{globalErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!allSent}
            data-testid="wf-cds-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
